// LivestreamOverlayCta.jsx
import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import LiveStreamCountDown from '../container/LiveStreamCountDown.js';
import PrivateShowTicketShop from '../container/PrivateShowTicketShop.js';
import UserAvatar from '../container/UserAvatar.js';
import PrivateShowTicketNotice from '../container/PrivateShowTicketNotice.js';
import WithLivestreamRating from '../container/WithLivestreamRating.js';

import TriggerPrivateNotificationModal from '../component/TriggerPrivateNotificationModal.jsx';
import Rating from '../component/Rating.jsx';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import { ModalId, ButtonId } from '../resource/mixpanel.js';
import {
  ExclusivePrivateStatus,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';
import numberWithCommas from '../resource/numberWithCommas.js';

import { color } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import DiamondIconSource from '../../img/ic-diamond-sm.svg';

export const LivestreamOverlayCta = ({
  streamId = null,
  meId = null,
  username = '',
  displayName = '',
  streamTitle = '',
  privateShowStatus = null,
  exclusivePrivateStatus = null,
  balance = 0,
  viewers = 0,
  price = null,
  exclusivePrice = null,
  priceDurationMinutes = 1,
  viewersMax = 0,
  viewersMin = 0,
  isDisplayNameEnabled = true,
  leaderboardUserIds = EMPTY_ARRAY,
  ticketNoticeIds = EMPTY_ARRAY,
  toggleModal = NULL_FUNCTION,
  addvalue = NULL_FUNCTION,
  unlockPrivateStream = NULL_FUNCTION,
  setModalTracking = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const mode = useMemo(() => {
    switch (true) {
      case [
        ExclusivePrivateStatus.PREPARING,
        ExclusivePrivateStatus.PERFORMING,
      ].includes(exclusivePrivateStatus): {
        return 'exclusive-private';
      }
      case [
        privateShowStatusConstant.PREPARING,
        privateShowStatusConstant.PERFORMING,
      ].includes(privateShowStatus): {
        return 'private-show';
      }
      case price != null: {
        return 'group-private';
      }
      default: {
        return;
      }
    }
  }, [exclusivePrivateStatus, price, privateShowStatus]);
  const isInsufficientDiamondForExclusivePrivate = balance < exclusivePrice;
  const mixpanelModalId =
    'exclusive-private' === mode
      ? isInsufficientDiamondForExclusivePrivate
        ? ModalId.LiveStream.ModalExclusivePrivatePurchaseDiamond
        : ModalId.LiveStream.ModalEnterExclusivePrivate
      : 'private-show' === mode
        ? ModalId.LiveStream.LivestreamShowPurchaseModal
        : 'group-private' === mode
          ? ModalId.LiveStream.PrivateLivestreamDetail
          : undefined;
  const hasTicketNoticeIds = ticketNoticeIds.length > 0;

  // Set modal tracking
  useEffect(() => {
    setModalTracking({
      modalId: mixpanelModalId,
    });
  }, [mixpanelModalId, setModalTracking]);

  if (mode == null) return null;

  if ('exclusive-private' === mode) {
    return (
      <TriggerPrivateNotificationModal
        titleI18nKey="stream_exclusive_waiting"
        body={
          <FlexCenterBox>
            <UserAvatarRow>
              <UserAvatarWrapper>
                <UserAvatar id={streamId} size={64} />
              </UserAvatarWrapper>
              <UserAvatarWrapper>
                <UserAvatar id={meId} size={64} />
              </UserAvatarWrapper>
            </UserAvatarRow>
            <UserDisplayName>{displayName || username}</UserDisplayName>
          </FlexCenterBox>
        }
        button={
          <PurchaseDiamondButton
            type="button"
            data-element_id={
              isInsufficientDiamondForExclusivePrivate
                ? ButtonId.LiveStream.LivestreamShowPurchaseModal
                : ButtonId.LiveStream.ButtonEnterExclusivePrivate
            }
            data-tracking_payload={{
              modalId: mixpanelModalId,
            }}
            onClick={() =>
              isInsufficientDiamondForExclusivePrivate
                ? addvalue()
                : unlockPrivateStream({ streamId })
            }
          >
            {t(
              isInsufficientDiamondForExclusivePrivate
                ? 'button_add_funds_for_stream'
                : 'button_enter_exclusive',
              {
                ns: TranslationNamespace.LIVESTREAM,
              }
            )}
          </PurchaseDiamondButton>
        }
      />
    );
  }

  const renderPrivateShowCta = () => (
    <>
      <PrivateShowTicketShop
        streamId={streamId}
        shouldShowCountdownWhenPerforming
      />
      <PrivateShowNotice
        data-element_id={ButtonId.LiveStream.ButtonLivestreamShowNotice}
        data-tracking_payload={{
          modalId: mixpanelModalId,
        }}
        onClick={() =>
          toggleModal({
            modalId: 'PrivateShowNoticeModal',
            isOpen: true,
          })
        }
      >
        {t('goal_label_notice', {
          ns: TranslationNamespace.LIVESTREAM,
        })}
      </PrivateShowNotice>
    </>
  );
  const renderPrivateStreamCta = () => (
    <>
      <StreamerAvatar>
        <UserAvatar id={streamId} size={64} />
      </StreamerAvatar>
      {isDisplayNameEnabled && displayName != null && (
        <CreatorDisplayName>{displayName}</CreatorDisplayName>
      )}
      <CreatorUsername>{`@${username}`}</CreatorUsername>
      <WithLivestreamRating id={streamId}>
        {({
          ratingStepSize,
          displayRating,
          displayRatingCount,
          rating,
          isDisplayRatingEnabled,
        }) =>
          !!rating && isDisplayRatingEnabled ? (
            <RatingSummary>
              <DisplayRating>{displayRating}</DisplayRating>
              <Rating
                value={displayRating || 0}
                max={100 / ratingStepSize}
                iconSize={20}
                isReadOnly
              />
              <DisplayRatingCount>{displayRatingCount}</DisplayRatingCount>
            </RatingSummary>
          ) : null
        }
      </WithLivestreamRating>
      {leaderboardUserIds.length >= viewersMin && (
        <ViewerAvatars>
          {leaderboardUserIds.slice(0, viewersMax).map(id => (
            <ViewerAvatar key={id}>
              <UserAvatar id={id} size={22} />
            </ViewerAvatar>
          ))}
        </ViewerAvatars>
      )}
      <CurrentViewers>
        {t('audience_in_private_stream', {
          ns: TranslationNamespace.LIVESTREAM,
          viewers,
        })}
      </CurrentViewers>
      <LiveStreamCountDown
        streamId={streamId}
        setModalTracking={NULL_FUNCTION}
        mixpanelModalId={mixpanelModalId}
        isOnLivestreamOverlayCta
      />
      <AvailableMinuteAndPrice>
        {t('info_remaining_time_private_stream', {
          ns: TranslationNamespace.LIVESTREAM,
          0:
            price != null && balance != null
              ? Math.floor((balance / price) * priceDurationMinutes)
              : 0,
        })}
        {price != null && (
          <PrivateStreamPrice>
            <PriceWithIcon>
              <img src={DiamondIconSource} alt="Diamond" aria-hidden />
              {numberWithCommas({ number: price })}
            </PriceWithIcon>
            {t('private_stream_per_second', {
              ns: TranslationNamespace.LIVESTREAM,
              second: priceDurationMinutes * 60,
            })}
          </PrivateStreamPrice>
        )}
      </AvailableMinuteAndPrice>
    </>
  );
  const isPrivateShow = 'private-show' === mode;

  return (
    <StyledLivestreamOverlayCta>
      <Header>
        {isPrivateShow
          ? t('show_show_time', {
              ns: TranslationNamespace.LIVESTREAM,
            })
          : streamTitle ||
            t('title_in_private_stream', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
      </Header>
      {isPrivateShow && hasTicketNoticeIds && (
        <TicketNoticeWrapper>
          <PrivateShowTicketNotice streamId={streamId} />
        </TicketNoticeWrapper>
      )}
      <Body hasPaddingTop={isPrivateShow ? !hasTicketNoticeIds : true}>
        {isPrivateShow ? renderPrivateShowCta() : renderPrivateStreamCta()}
      </Body>
    </StyledLivestreamOverlayCta>
  );
};

LivestreamOverlayCta.propTypes = {
  streamId: PropTypes.string,
  meId: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  privateShowStatus: PropTypes.string,
  exclusivePrivateStatus: PropTypes.string,
  streamTitle: PropTypes.string,
  balance: PropTypes.number,
  viewers: PropTypes.number,
  price: PropTypes.number,
  exclusivePrice: PropTypes.number,
  priceDurationMinutes: PropTypes.number,
  viewersMax: PropTypes.number,
  viewersMin: PropTypes.number,
  isDisplayNameEnabled: PropTypes.bool,
  leaderboardUserIds: PropTypes.array,
  ticketNoticeIds: PropTypes.array,
  toggleModal: PropTypes.func,
  addvalue: PropTypes.func,
  unlockPrivateStream: PropTypes.func,
  setModalTracking: PropTypes.func,
};

const StyledLivestreamOverlayCta = styled.div`
  border-radius: 8px;
  width: 320px;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 8px 16px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 600;
  background-color: ${color.neutral[30]};
`;

const TicketNoticeWrapper = styled.div`
  padding: 8px 0px;
  background-color: ${color.neutral[10]};
`;

const Body = styled.div.attrs(({ hasPaddingTop }) => ({
  style: {
    padding: `${hasPaddingTop ? 20 : 0}px 16px 20px 16px`,
  },
}))`
  text-align: center;
  background-color: ${color.neutral[10]};
`;

const StreamerAvatar = styled.div`
  margin: 0px auto;
  margin-bottom: 8px;
  width: 64px;
  height: 64px;
`;

const CreatorDisplayName = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: ${color.neutral[100]};
`;

const CreatorUsername = styled(CreatorDisplayName)`
  margin-bottom: 8px;
  color: ${color.neutral[75]};
  font-weight: 400;
`;

const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DisplayRating = styled.div`
  margin-right: 4px;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.5;
  color: ${color.neutral[100]};
`;

const DisplayRatingCount = styled.div`
  margin-left: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: ${color.neutral[75]};
  ::before {
    content: '(';
  }
  ::after {
    content: ')';
  }
`;

const ViewerAvatars = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ViewerAvatar = styled.div`
  z-index: 0;
  margin-left: -4px;
  border-radius: 50%;
  border: 1px solid transparent;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.neutral[20]};
`;

const CurrentViewers = styled.div`
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.neutral[65]};
`;

const AvailableMinuteAndPrice = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  color: ${color.neutral[65]};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
`;

const PrivateStreamPrice = styled.div`
  margin-left: 8px;
  display: flex;
  align-items: center;
  color: ${color.neutral[65]};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
`;

const PriceWithIcon = styled.div`
  margin-right: 4px;
  display: flex;
  align-items: center;
  color: ${color.tealBlue};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  > img {
    margin-right: 2px;
    width: 12px;
    height: 12px;
    object-fit: contain;
  }
`;

const PrivateShowNotice = styled.button`
  ${ResetButtonStyle};
  margin-top: 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: ${color.tealBlue};
  text-decoration: underline;
`;

const FlexCenterBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 8px;
`;

const UserAvatarWrapper = styled.div`
  padding: 6px;
  margin-left: -24px;
  :first-child {
    margin-left: 0;
    z-index: 1;
  }
`;

const UserAvatarRow = styled.div`
  display: flex;
`;

const UserDisplayName = styled.div`
  display: flex;
  align-items: center;
  color: white;
  text-align: center;
  font-style: normal;
  font-weight: 600;
  line-height: 150%; /* 24px */
  letter-spacing: 0.012px;
  font-size: 14px;
  ${media.mobile`
    font-size: 12px;
  `}
`;

const PurchaseDiamondButton = styled.button`
  display: flex;
  height: 40px;
  padding: 2px 8px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid ${color.tealBlue};

  color: ${color.tealBlue};
  font-weight: 600;
  line-height: 150%; /* 30px */
  letter-spacing: 0.02px;
  font-size: 20px;
  ${media.mobile`
    font-size: 18px;
  `}
`;

export default LivestreamOverlayCta;
